import Api, {HttpVerbs} from './Api';
import {UrlTypes} from 'src/types/UrlTypes';
import {SetupModel} from 'src/types/SetupModel';
import Settings from 'src/Settings';
import AccountConstants from 'src/constants/AccountConstants';
import {store} from '../redux/store';
import {Location} from '../types/serverTypes/Location';
import {Account} from 'src/types/serverTypes/Account';

export type PayrollDeductInfo = {
  payCycle: string;
  spendLimit: number;
  availableBalance: number;
  resetDate: string;
};

export type PayrollDeductResponse = {
  status: string;
  msg: string;
  data: PayrollDeductInfo;
};

export type RequestResponse = {
  status: string;
  msg: string;
  data: Record<string, any>;
};

export class GmaApi {
  setupAccount(setupModel: SetupModel) {
    const url = this.getFullUrl('/mka/updins');
    return Api.fetch(url, setupModel, 'POST');
  }

  confirmEmailAddress(
    marketAccount: string,
    emailAddress: string,
    emailVerificationToken: string,
  ) {
    const url = this.getFullUrl(`/mka/${marketAccount}`);
    const postObject = {
      commandType: 'VERIFY_EMAIL_ADDRESS',
      emailAddress,
      emailVerificationToken,
    };
    return Api.fetch(url, postObject, 'PUT');
  }

  /** not used */
  async loginGma(username: string, password: string, buildType: string) {
    // const url = this.getFullUrl('/mka/show', {
    //   apptype: buildType,
    // });
    // const body = {
    //   email: username,
    //   password,
    //   apptype: buildType,
    // };
    // const response = (await Api.fetch(url, body, 'POST')) as RequestResponse;
    // return this.checkIfAccountDisabled(response);
    return {username, password, buildType} as unknown as RequestResponse;
  }

  getGmaQR(marketAccount: string) {
    const url = this.getFullUrl('/mki/showqr', {
      marketaccount: marketAccount,
    });
    return Api.fetch(url, null, 'GET');
  }

  loadPurchaseHistory(accountId: string, page: number, pageSize: number) {
    const url = this.getFullUrl('/transactions/v3/show', {
      marketAccount: accountId,
      page,
      pageSize,
    });
    return Api.fetch(url, null, 'GET');
  }

  getCurrentPrivacyVersion(accountId: string) {
    if (!accountId || accountId == '') {
      return null;
    }

    const url = this.getFullPrivacyUrl(`/accounts/${accountId}/privacy`);
    return Api.fetchGateway(url, null, 'GET');
  }

  async getBalance(accountId: string) {
    const url = this.getFullUrl('/mka/show', {
      id: accountId,
      app: Settings.buildType,
    });
    const response = await Api.fetch(url, null, 'GET');
    return this.checkIfAccountDisabled(response) as any;
  }

  getRedemptionThresholds(accountId: string) {
    const url = this.getFullUrl('/org/redemptionthreshold', {
      marketaccount: accountId,
    });
    return Api.fetch(url, null, 'GET');
  }

  setPin(accountId: string, pin: string) {
    const url = this.getFullUrl('/mka/resetpin');
    const postObject = {
      id: accountId,
      pin,
    };
    return Api.fetch(url, postObject, 'POST');
  }

  loginMki(type: number, code: string) {
    const url = this.getFullUrl('/mka/showbyvalue', {
      type,
    });
    const postObject = {
      value: code,
    };
    return Api.fetch(url, postObject, 'POST');
  }

  loadPromotions(mkaId: string) {
    const url = this.getFullUrl(`/mka/${mkaId}/promotions`);
    return Api.fetch(url, null, 'GET');
  }

  async getAccountByBroadcastId(
    broadcastId: string,
    accountId: string,
  ): Promise<Account> {
    const params: Record<string, string> = {
      broadcastId,
    };

    if (accountId && accountId !== '-1') {
      params.accountId = accountId;
    }

    const url = this.getFullPrivacyUrl('/devices/beacon-lookup', params);
    return await Api.fetchGateway(url, null, 'GET');
  }

  //TODO:SM: It seems legacy code, api doesn't support 'type' as param
  async getAccountFromScannedId(
    scannedId: string,
    accountId: string,
    type: string,
  ): Promise<Account> {
    const params: Record<string, string> = {
      broadcastId: scannedId,
      type,
    };

    if (accountId && accountId !== '-1') {
      params.accountId = accountId;
    }
    const url = this.getFullPrivacyUrl('/devices/beacon-lookup', params);
    return await Api.fetchGateway(url, null, 'GET');
  }

  //TODO:SM: dublicate fetchLocation on PlatformApi, need refactor
  fetchLocation(locationId: string): Promise<Location> {
    const url = Api.getGatewayUrl(`platform/locations/${locationId}`);
    return Api.fetchGateway(url, null, HttpVerbs.Get) as Promise<Location>;
  }

  removeLocationLink(marketAccount: string, location: string) {
    const params: Record<string, string> = {
      marketAccount,
      location,
    };
    const url = this.getFullUrl('/location/linked', params);
    return Api.fetch(url, null, 'DELETE');
  }

  getRecentOrders(marketAccountId: string, locationId: string) {
    const url = this.getFullUrl('/sales/recent', {
      marketAccountId,
      locationId,
    });
    return Api.fetch(url, null, 'GET');
  }

  loginQR(qrCode: string) {
    return this.loginMki(2, qrCode);
  }

  loginMarketCard(code: string) {
    return this.loginMki(0, code);
  }

  retrievePayrollDeductData(
    accountId: string,
    payrollId: string,
    currentDate: string,
  ): Promise<PayrollDeductResponse> {
    const url = this.getFullUrl('/mka/payrollDeductInfo', {
      accountId,
      payrollId,
      currentDate,
    });
    return Api.fetch(
      url,
      null,
      'GET',
    ) as unknown as Promise<PayrollDeductResponse>;
  }

  checkIfAccountDisabled(response: RequestResponse) {
    if (response.status === 'ok' && response.data.isdisabled === 'Y') {
      response.status = 'err';
      response.msg = AccountConstants.ENTITY_NOT_FOUND;
    }

    return response;
  }

  getFullUrl(path: string, params?: Record<string, string | number>) {
    const baseUrl = store.getState().environment.serviceUrls[UrlTypes.gmaapi];
    return Api.getFullUrl(baseUrl, path, params);
  }

  getFullPrivacyUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined | null>,
  ) {
    const baseUrl = Api.getGatewayUrl(UrlTypes.accountapi);
    return Api.getFullUrl(baseUrl, path, params);
  }

  acceptPrivacy(accountId: string, version: string) {
    const url = this.getFullUrl(
      `/mka/${accountId}/acceptprivacyversion?version=${version}`,
    );
    return Api.fetch(url, null, 'POST');
  }
}
export default new GmaApi();
